'use client';

/**
 * Managed option lists — the wire.
 *
 *   useOptionList(key, opts?)         GET  /option-lists/{key}[?include_hidden&include_archived]
 *   useResolvedOptions(key, keys)     POST /option-lists/{key}/resolve — labels of keys a record holds
 *   useOptionListMutations(key)       add / edit / archive / restore / hide / show
 *
 * `document_types` goes through `documentTypesAdapter` (the catalogue's own
 * endpoints) unless the caller passes another adapter. Every write
 * invalidates the list key, so every dropdown on the page re-reads it — the
 * value someone just added appears in the next dropdown too.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '@/lib/api';

import { adapterFor } from './index';
import type {
  ApiCall,
  ListQueryOptions,
  OptionItem,
  OptionList,
  OptionListAdapter,
  OptionValueInput,
  OptionValuePatch,
  ResolvedValue,
} from './types';

export const optionListKey = (listKey: string, adapterId: string, opts: ListQueryOptions = {}) =>
  ['optionList', listKey, adapterId, opts.includeHidden ? 'h' : '-', opts.includeArchived ? 'a' : '-'] as const;

const listRoot = (listKey: string) => ['optionList', listKey] as const;

export function useOptionList(
  listKey: string,
  opts: ListQueryOptions & { adapter?: OptionListAdapter; enabled?: boolean } = {},
) {
  const api = useApi() as unknown as ApiCall;
  const adapter = opts.adapter ?? adapterFor(listKey);
  const q = { includeHidden: opts.includeHidden, includeArchived: opts.includeArchived };
  return useQuery<OptionList, Error>({
    queryKey: optionListKey(listKey, adapter.id, q),
    queryFn: () => adapter.fetch(api, listKey, q),
    staleTime: 60_000,
    enabled: opts.enabled ?? true,
  });
}

/** Labels of keys a record already holds — archived and hidden ones too. */
export function useResolvedOptions(listKey: string, keys: readonly string[], enabled = true) {
  const api = useApi() as unknown as ApiCall;
  const sorted = [...keys].sort();
  return useQuery<Record<string, ResolvedValue>, Error>({
    queryKey: ['optionList', listKey, 'resolve', sorted.join('|')],
    queryFn: async () =>
      (
        await api<{ list_key: string; values: Record<string, ResolvedValue> }>(
          `/option-lists/${encodeURIComponent(listKey)}/resolve`,
          { method: 'POST', body: { keys: sorted } },
        )
      ).values,
    enabled: enabled && sorted.length > 0 && listKey !== 'document_types',
    staleTime: 60_000,
  });
}

export function useOptionListMutations(listKey: string, adapter?: OptionListAdapter) {
  const api = useApi() as unknown as ApiCall;
  const qc = useQueryClient();
  const a = adapter ?? adapterFor(listKey);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: listRoot(listKey) });
    if (listKey === 'document_types') void qc.invalidateQueries({ queryKey: ['brief'] });
  };
  const need = <K extends keyof OptionListAdapter>(k: K) => {
    const fn = a[k];
    if (!fn) throw new Error('This list cannot be changed here.');
    return fn as NonNullable<OptionListAdapter[K]>;
  };

  const create = useMutation<OptionItem, Error, OptionValueInput>({
    mutationFn: (input) => need('create')(api, listKey, input),
    onSuccess: refresh,
  });
  const update = useMutation<OptionItem, Error, { item: OptionItem; patch: OptionValuePatch }>({
    mutationFn: ({ item, patch }) => need('update')(api, listKey, item, patch),
    onSuccess: refresh,
  });
  const archive = useMutation<OptionItem, Error, OptionItem>({
    mutationFn: (item) => need('archive')(api, listKey, item),
    onSuccess: refresh,
  });
  const restore = useMutation<OptionItem, Error, OptionItem>({
    mutationFn: (item) => need('restore')(api, listKey, item),
    onSuccess: refresh,
  });
  const setHidden = useMutation<OptionItem, Error, { item: OptionItem; hidden: boolean }>({
    mutationFn: ({ item, hidden }) => need('setHidden')(api, listKey, item, hidden),
    onSuccess: refresh,
  });
  return {
    create,
    update,
    archive,
    restore,
    setHidden,
    supports: {
      create: Boolean(a.create),
      update: Boolean(a.update),
      archive: Boolean(a.archive),
      restore: Boolean(a.restore),
      setHidden: Boolean(a.setHidden),
    },
  };
}
